import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const Stars = ({ n }) => (
    <span className="inline-flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={14} className={i <= n ? "fill-[#B8860B] text-[#B8860B]" : "text-[#d9d4ca]"} />
        ))}
    </span>
);

export const ReviewsSection = ({ slug }) => {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({ name: "", city: "", rating: 5, title: "", body: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        api.get(`/reviews/${slug}`).then((r) => setData(r.data)).catch(() => {});
    }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data: res } = await api.post("/reviews", { product_slug: slug, ...form, rating: Number(form.rating) });
            toast.success(res.message);
            setSent(true);
        } catch { toast.error("Could not submit review"); } finally { setBusy(false); }
    };

    if (!data) return null;

    return (
        <section className="pb-16">
            <div className="container-x max-w-[820px]">
                <h2 className="font-serif text-[28px] text-[#003E4D] mb-1">Customer reviews</h2>
                {data.count > 0 ? (
                    <p className="text-[14px] text-[#4a5568] flex items-center gap-2" data-testid="reviews-summary">
                        <Stars n={Math.round(data.average)} /> {data.average} / 5 · {data.count} verified review{data.count > 1 ? "s" : ""}
                    </p>
                ) : (
                    <p className="text-[14px] text-[#4a5568]">No reviews for this model yet — be the first.</p>
                )}
                <div className="mt-6 space-y-4" data-testid="reviews-list">
                    {data.reviews.map((r) => (
                        <div key={r.id} className="p-6 bg-white border border-[#e2ddd4] rounded-2xl">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <p className="font-medium text-[15px]">{r.title}</p>
                                <Stars n={r.rating} />
                            </div>
                            <p className="mt-2 text-[14.5px] text-[#333] leading-relaxed">{r.body}</p>
                            <p className="mt-3 text-[12.5px] text-[#4a5568]">{r.name}{r.city ? ` · ${r.city}` : ""}{r.verified ? " · ✓ Verified purchase" : ""}</p>
                        </div>
                    ))}
                </div>
                <details className="mt-6 bg-white border border-[#e2ddd4] rounded-2xl px-6 py-5">
                    <summary className="cursor-pointer font-medium text-[15px]" data-testid="write-review-toggle">Write a review</summary>
                    {sent ? (
                        <p className="mt-4 text-[14.5px] text-[#1a5c3a]" data-testid="review-thanks">✓ Thank you! Your review is pending moderation.</p>
                    ) : (
                        <form onSubmit={submit} className="mt-4 space-y-3">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="review-name" className="border border-[#e2ddd4] rounded-xl px-4 py-3 text-[14.5px]" />
                                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" data-testid="review-city" className="border border-[#e2ddd4] rounded-xl px-4 py-3 text-[14.5px]" />
                            </div>
                            <div className="flex items-center gap-2 text-[14px]">
                                Rating:
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                                        <Star size={20} className={n <= form.rating ? "fill-[#B8860B] text-[#B8860B]" : "text-[#d9d4ca]"} />
                                    </button>
                                ))}
                            </div>
                            <input required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Review headline" data-testid="review-title" className="w-full border border-[#e2ddd4] rounded-xl px-4 py-3 text-[14.5px]" />
                            <textarea required minLength={10} rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Your experience — sound quality, comfort, battery, service" data-testid="review-body" className="w-full border border-[#e2ddd4] rounded-xl px-4 py-3 text-[14.5px]" />
                            <button disabled={busy} className="btn-primary !text-[13.5px]" data-testid="review-submit">{busy ? "Submitting…" : "Submit Review"}</button>
                        </form>
                    )}
                </details>
            </div>
        </section>
    );
};

export default ReviewsSection;
